import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const shots = [
  { img: "/assets/gallery/unit-corridor.webp", plate: "Unit Corridor · Numbered Kitchens" },
  { img: "/assets/gallery/unit-entry.webp", plate: "Kitchen 01 · Fire-Rated Entry" },
  { img: "/assets/gallery/kitchen-corridor.webp", plate: "Cookline · Service Corridor" },
  { img: "/assets/gallery/prep-line.webp", plate: "Prep Line · Plating Station" },
  { img: "/assets/gallery/equipment-lineup.webp", plate: "Equipment Lineup · Ranges & Chillers" },
  { img: "/assets/gallery/combi-oven.webp", plate: "UNOX Cheftop · Combi Oven" },
  { img: "/assets/gallery/combi-oven-closeup.webp", plate: "Combi Station · Under Hood" },
  { img: "/assets/gallery/extraction-hood.webp", plate: "Extraction & Ventilation" },
  { img: "/assets/gallery/freezers.webp", plate: "Cold Storage · Upright Freezers" },
  { img: "/assets/gallery/fridge-wall.webp", plate: "Chillers & Work Tables" },
  { img: "/assets/gallery/chiller-drawers.webp", plate: "Under-Counter Chiller Drawers" },
  { img: "/assets/gallery/spray-sink.webp", plate: "Pot Wash · Spray Rinse" },
  { img: "/assets/gallery/handwash-basin.webp", plate: "Hands-Free Wash Basin" },
  { img: "/assets/gallery/wash-station.webp", plate: "Double Sink · Drainboard" },
  { img: "/assets/gallery/sink-station.webp", plate: "Sink Station · Food-Grade Stainless" },
];

export default function Gallery() {
  const ref = useRef(null);
  const trackRef = useRef(null);
  const [dist, setDist] = useState(1200);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const x = useTransform(scrollYProgress, [0, 1], [0, -dist]);

  useEffect(() => {
    const measure = () => {
      if (trackRef.current) {
        setDist(Math.max(0, trackRef.current.scrollWidth - window.innerWidth + 48));
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  return (
    <section id="gallery" ref={ref} className="relative h-[520vh]" data-testid="gallery-section">
      <div className="sticky top-0 flex h-screen flex-col justify-center overflow-hidden">
        <div className="mx-auto mb-10 w-full max-w-7xl px-5 md:px-8">
          <p className="text-[11px] font-semibold uppercase tracking-[0.34em] text-gold" data-testid="gallery-eyebrow">Inside The Kitchen</p>
          <h2 className="mt-4 font-serif text-4xl font-medium tracking-tight text-white md:text-5xl" data-testid="gallery-heading">
            Walk the <span className="italic text-gold">corridor.</span>
          </h2>
          <p className="mt-4 max-w-md text-sm font-light leading-relaxed text-white/60">
            Keep scrolling — take a walk through the real Royal Express facility in Dubai Investment Park.
          </p>
        </div>

        <motion.div ref={trackRef} style={{ x }} className="flex w-max gap-6 pl-5 md:pl-8" data-testid="gallery-track">
          {shots.map((s, i) => (
            <figure
              key={s.plate}
              className="group relative h-[50vh] w-[72vw] shrink-0 overflow-hidden rounded-2xl border border-gold/20 sm:w-[48vw] lg:w-[30vw]"
            >
              <img
                src={s.img}
                alt={`Royal Express DIP facility — ${s.plate}`}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-black/15" aria-hidden="true" />
              <figcaption className="absolute inset-x-5 bottom-5 flex items-center justify-between gap-3">
                <span
                  className="rounded-sm border border-gold/50 bg-black/70 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-gold backdrop-blur-md md:text-[10px]"
                  data-testid={`gallery-plate-${i}`}
                >
                  {s.plate}
                </span>
                <span className="shrink-0 font-serif text-sm italic text-white/50">
                  {String(i + 1).padStart(2, "0")} / {String(shots.length).padStart(2, "0")}
                </span>
              </figcaption>
            </figure>
          ))}
        </motion.div>

        <div className="mx-auto mt-10 w-full max-w-7xl px-5 md:px-8">
          <div className="h-px w-full bg-gold/15">
            <motion.div style={{ scaleX: scrollYProgress }} className="h-px origin-left bg-gold" data-testid="gallery-progress" />
          </div>
        </div>
      </div>
    </section>
  );
}
